import React, { useState } from 'react';
import {
    Search,
    Filter,
    ArrowRight,
    Play,
    Users,
    Lightbulb,
    Star,
    Heart,
    Target,
    Headphones,
    UserCheck,
    BarChart3,
    Code,
    MapPin,
    Send
} from 'lucide-react';
import PublicPageLayout from '../../layouts/PublicPageLayout';

export default function CareersPage() {
    const [searchTerm, setSearchTerm] = useState('');

    const values = [
        {
            title: "Esprit d'équipe",
            desc: "Nous avançons ensemble et nous nous soutenons.",
            icon: Users,
            bgColor: 'bg-blue-50',
            iconColor: 'text-blue-600',
        },
        {
            title: 'Innovation',
            desc: 'Nous encourageons les idées nouvelles et l’amélioration continue.',
            icon: Lightbulb,
            bgColor: 'bg-emerald-50',
            iconColor: 'text-emerald-600',
        },
        {
            title: 'Excellence',
            desc: 'Nous visons l’excellence dans tout ce que nous faisons.',
            icon: Star,
            bgColor: 'bg-amber-50',
            iconColor: 'text-amber-500',
        },
        {
            title: 'Intégrité',
            desc: 'Nous agissons avec honnêteté, respect et transparence.',
            icon: Heart,
            bgColor: 'bg-purple-50',
            iconColor: 'text-purple-600',
        },
        {
            title: 'Impact',
            desc: 'Nous avons un impact positif sur nos clients et nos communautés.',
            icon: Target,
            bgColor: 'bg-rose-50',
            iconColor: 'text-rose-500',
        },
    ];

    const jobOffers = [
        {
            id: 1,
            title: 'Conseiller(ère) Service Client',
            location: 'Yaoundé, Cameroun',
            type: 'Temps plein',
            badgeBg: 'bg-blue-50 text-blue-600',
            icon: Headphones,
            iconBg: 'bg-blue-50 text-blue-600',
            description:
                'Répondre aux appels et messages des clients, résoudre leurs demandes et offrir une expérience exceptionnelle.',
        },
        {
            id: 2,
            title: "Superviseur(e) d'équipe",
            location: 'Yaoundé, Cameroun',
            type: 'Temps plein',
            badgeBg: 'bg-emerald-50 text-emerald-600',
            icon: UserCheck,
            iconBg: 'bg-emerald-50 text-emerald-600',
            description:
                'Encadrer, accompagner et motiver une équipe de conseillers pour atteindre les objectifs de performance.',
        },
        {
            id: 3,
            title: 'Analyste Qualité',
            location: 'Yaoundé, Cameroun',
            type: 'Temps plein',
            badgeBg: 'bg-amber-50 text-amber-600',
            icon: BarChart3,
            iconBg: 'bg-amber-50 text-amber-600',
            description:
                'Évaluer les interactions, assurer le respect des standards et proposer des axes d’amélioration.',
        },
        {
            id: 4,
            title: 'Développeur(euse) Logiciel',
            location: 'Montréal, Canada',
            type: 'Temps plein',
            badgeBg: 'bg-purple-50 text-purple-600',
            icon: Code,
            iconBg: 'bg-purple-50 text-purple-600',
            description:
                'Concevoir et développer des solutions innovantes pour améliorer nos plateformes et outils internes.',
        },
    ];

    return (
        <PublicPageLayout>
            {/* ================= HERO SECTION ================= */}
            <section className="relative w-full bg-[#F3F6FC] overflow-hidden pt-2 pb-10 lg:py-0  flex items-center">

                {/* Conteneur principal aligné */}
                <div className="w-full mt-6  max-w-7xl mx-auto px-6 lg:px-12 relative z-10">
                    <div className="w-full lg:w-[45%] space-y-2">
                        <span className="text-xs font-extrabold text-blue-600 tracking-wider uppercase">
                            CARRIÈRES
                        </span>

                        <h1 className="text-3xl lg:text-[38px] font-extrabold text-slate-900 leading-[1.18] tracking-tight">
                            Rejoignez une équipe <br />
                            passionnée et construisons <br />
                            l'avenir de la relation client, <br />
                            <span className="text-blue-600">
                                à la vitesse de la lumière<span className="text-amber-400">.</span>
                            </span>
                        </h1>

                        <p className="text-slate-600 text-sm leading-relaxed max-w-md">
                            Chez IZI Call Center, nous croyons que nos talents sont notre force. Ensemble, nous innovons, nous grandissons et nous offrons des expériences client exceptionnelles.
                        </p>

                        <div className="flex flex-wrap items-center gap-4 pt-2">
                            <button className="bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs px-6 py-3.5 rounded-xl shadow-md shadow-blue-500/20 transition-all flex items-center gap-2">
                                Voir nos offres d'emploi <ArrowRight size={16} />
                            </button>

                            <button className="bg-white hover:bg-slate-50 text-blue-600 border border-blue-200 font-bold text-xs px-6 py-3.5 rounded-xl shadow-sm transition-all flex items-center gap-2">
                                Découvrez notre culture <Play size={14} className="fill-blue-600" />
                            </button>
                        </div>
                    </div>
                </div>

                <div
                    className="mt-10 lg:mt-0 lg:absolute lg:top-0  lg:right-0 lg:w-[52%] lg:h-full flex justify-end items-stretch z-0 pointer-events-none"
                    style={{
                        backgroundImage: `url('/careers/hero.png')`,
                        backgroundSize: 'contain',
                        backgroundPosition: 'right top',
                        backgroundRepeat: 'no-repeat',
                    }}
                >
                    {/* Plus besoin de la balise img */}
                </div>
            </section>

            {/* Contenu secondaire avec padding standard */}
            <div className="max-w-7xl mx-auto px-6 lg:px-12 py-10 space-y-10">

                {/* ================= SECTION NOS VALEURS ================= */}
                <div className="bg-white rounded-3xl border border-slate-200/70 p-6 shadow-sm">
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6 divide-y sm:divide-y-0 sm:divide-x divide-slate-100">
                        {values.map((val, index) => {
                            const Icon = val.icon;
                            return (
                                <div
                                    key={index}
                                    className={`flex items-start gap-3 ${index !== 0 ? 'pt-4 sm:pt-0 sm:pl-4' : ''}`}
                                >
                                    <div className={`w-10 h-10 rounded-2xl ${val.bgColor} ${val.iconColor} flex items-center justify-center shrink-0`}>
                                        <Icon size={20} />
                                    </div>
                                    <div className="space-y-1">
                                        <h4 className="text-xs font-bold text-slate-900">{val.title}</h4>
                                        <p className="text-[11px] text-slate-500 leading-relaxed">{val.desc}</p>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* ================= SECTION NOS OPPORTUNITÉS ================= */}
                <div className="space-y-6">

                    {/* Header + Recherche */}
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div>
                            <h2 className="text-2xl font-extrabold text-slate-900">Nos opportunités</h2>
                            <p className="text-slate-500 text-xs mt-1">
                                Découvrez nos postes ouverts et trouvez celui qui vous correspond.
                            </p>
                        </div>

                        {/* Filtres & Recherche */}
                        <div className="flex items-center gap-3">
                            <div className="relative w-full sm:w-64">
                                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                                <input
                                    type="text"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    placeholder="Rechercher un poste..."
                                    className="w-full bg-white border border-slate-200/80 rounded-xl pl-9 pr-3 py-2 text-xs outline-none focus:border-blue-500 transition-all shadow-sm"
                                />
                            </div>

                            <button className="bg-white border border-slate-200/80 hover:bg-slate-50 text-slate-700 text-xs font-bold px-4 py-2 rounded-xl shadow-sm flex items-center gap-2 transition-colors shrink-0">
                                <Filter size={14} className="text-blue-600" />
                                Filtrer
                            </button>
                        </div>
                    </div>

                    {/* Grille des offres */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                        {jobOffers.map((job) => {
                            const Icon = job.icon;
                            return (
                                <div
                                    key={job.id}
                                    className="bg-white rounded-3xl border border-slate-200/70 p-5 shadow-sm hover:shadow-md transition-all flex flex-col justify-between space-y-4"
                                >
                                    <div className="space-y-3">
                                        <div className="flex items-center justify-between gap-2">
                                            <div className={`w-10 h-10 rounded-2xl ${job.iconBg} flex items-center justify-center shrink-0`}>
                                                <Icon size={20} />
                                            </div>
                                        </div>

                                        <div>
                                            <h3 className="text-sm font-bold text-slate-900 leading-snug">{job.title}</h3>
                                            <div className="flex items-center gap-1.5 text-slate-400 text-[11px] mt-1 font-medium">
                                                <MapPin size={12} />
                                                <span>{job.location}</span>
                                            </div>
                                        </div>

                                        <div>
                                            <span className={`inline-block px-2.5 py-1 rounded-md text-[10px] font-bold ${job.badgeBg}`}>
                                                {job.type}
                                            </span>
                                        </div>

                                        <p className="text-xs text-slate-500 leading-relaxed pt-1">
                                            {job.description}
                                        </p>
                                    </div>

                                    <div className="pt-2 border-t border-slate-100">
                                        <a
                                            href={`#job-${job.id}`}
                                            className="inline-flex items-center gap-1.5 text-xs font-bold text-blue-600 hover:text-blue-700 transition-colors group"
                                        >
                                            Voir l'offre
                                            <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                                        </a>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                </div>

                {/* ================= BANNIÈRE CANDIDATURE SPONTANÉE ================= */}
                <div className="bg-[#031533] rounded-3xl p-6 lg:p-8 text-white flex flex-col md:flex-row items-center justify-between gap-6 shadow-lg">
                    <div className="flex items-center gap-4 text-center md:text-left">
                        <div className="w-12 h-12 rounded-2xl bg-blue-600 flex items-center justify-center shrink-0">
                            <Send size={22} className="text-white" />
                        </div>
                        <div>
                            <h3 className="text-lg lg:text-xl font-bold">Vous ne trouvez pas le poste idéal ?</h3>
                            <p className="text-slate-400 text-xs lg:text-sm mt-0.5">
                                Envoyez-nous votre candidature spontanée. Nous sommes toujours à la recherche de nouveaux talents !
                            </p>
                        </div>
                    </div>

                    <button className="bg-white hover:bg-slate-100 text-blue-600 font-bold text-xs px-6 py-3.5 rounded-xl shadow-sm transition-colors flex items-center gap-2 whitespace-nowrap shrink-0">
                        Envoyer ma candidature spontanée <ArrowRight size={14} />
                    </button>
                </div>

            </div>
        </PublicPageLayout>
    );
}